import React, { useState } from 'react';
import { Button, Checkbox, Dropdown, Form, Input } from 'semantic-ui-react';
import { PeriodicTask } from '../types/PeriodicTask';
import { safeParseJson } from '../utils/json-utils';
import { processDateArithmetic } from '../utils/date-utils';
import ParameterDisplay from './ParameterDisplay';

const EditTaskForm: React.FC<{
  task: PeriodicTask | null;
  onSave: (data: any) => void;
  onCancel: () => void;
  loading: boolean;
}> = ({ task, onSave, onCancel, loading }) => {
  
  // For registry tasks, get default_kwargs from registry_task
  // For periodic tasks, use kwargs from the task
  const getInitialKwargs = () => {
    // Check if this is a registry task - could be passed as registry_task directly or nested
    const registryTask = (task as any)?.registry_task || (task as any);
    
    // Check if it has default_kwargs (registry task) or kwargs (periodic task)
    if (registryTask?.default_kwargs) {
      // Initialize with default_kwargs, preserving null values as empty strings for editing
      const defaultKwargs = registryTask.default_kwargs;
      const initializedKwargs: Record<string, any> = {};
      Object.keys(defaultKwargs).forEach(key => {
        initializedKwargs[key] = defaultKwargs[key] !== null && defaultKwargs[key] !== undefined 
          ? defaultKwargs[key] 
          : '';
      });
      return JSON.stringify(initializedKwargs, null, 2);
    }
    // Fall back to regular kwargs parsing for periodic tasks
    return safeParseJson(task?.kwargs);
  };
  
  const [formData, setFormData] = useState({
    name: task?.name || '',
    description: task?.description || '',
    enabled: task?.enabled || false,
    task: task?.task || '',
    schedule_type: task?.interval ? 'interval' : task?.crontab ? 'crontab' : 'none',
    interval_every: task?.interval?.every || '',
    interval_period: task?.interval?.period || 'minutes',
    crontab_minute: task?.crontab?.minute || '',
    crontab_hour: task?.crontab?.hour || '',
    crontab_day_of_week: task?.crontab?.day_of_week || '',
    crontab_day_of_month: task?.crontab?.day_of_month || '',
    crontab_month_of_year: task?.crontab?.month_of_year || '',
    crontab_timezone: 'UTC',
    args: task?.args || '[]',
    kwargs: getInitialKwargs(),
  });

  const handleSubmit = () => {
    let processedKwargs = formData.kwargs;

    if (processedKwargs && processedKwargs.trim()) {
      processedKwargs = processDateArithmetic(processedKwargs);
    }

    try {
      if (processedKwargs && processedKwargs.trim()) {
        JSON.parse(processedKwargs);
      }
    } catch (error) {
      alert('Invalid JSON format for Parameters. Please enter a valid JSON object.');
      return;
    }

    let scheduleData: any = {};

    if (formData.schedule_type === 'interval') {
      scheduleData.interval = {
        every: parseInt(formData.interval_every.toString()) || 1,
        period: formData.interval_period,
      };
      scheduleData.crontab = null;
    } else if (formData.schedule_type === 'crontab') {
      scheduleData.crontab = {
        minute: formData.crontab_minute || '*',
        hour: formData.crontab_hour || '*',
        day_of_week: formData.crontab_day_of_week || '*',
        day_of_month: formData.crontab_day_of_month || '*',
        month_of_year: formData.crontab_month_of_year || '*',
        timezone: formData.crontab_timezone,
      };
      scheduleData.interval = null;
    } else if (formData.schedule_type === 'none') {
      scheduleData.interval = null;
      scheduleData.crontab = null;
    }

    onSave({
      name: formData.name,
      task: formData.task,
      enabled: formData.enabled,
      description: formData.description,
      args: formData.args || '[]',
      kwargs: processedKwargs,
      ...scheduleData,
    });
  };

  return (
    <Form>
      <Form.Field>
        <label>Name</label>
        <Input
          value={formData.name}
          onChange={(_, { value }) => setFormData(prev => ({ ...prev, name: value }))}
          placeholder="Task name"
        />
      </Form.Field>

      <Form.Field>
        <label>Description</label>
        <Input
          value={formData.description}
          onChange={(_, { value }) => setFormData(prev => ({ ...prev, description: value }))}
          placeholder="Task description"
        />
      </Form.Field>

      <Form.Field>
        <label>Schedule Type</label>
        <Dropdown
          value={formData.schedule_type}
          onChange={(_, { value }) => {
            const newScheduleType = value as string;
            setFormData(prev => ({
              ...prev,
              schedule_type: newScheduleType,
              // Clear the other schedule type's values when switching
              ...(newScheduleType === 'interval'
                ? {
                    crontab_minute: '',
                    crontab_hour: '',
                    crontab_day_of_week: '',
                    crontab_day_of_month: '',
                    crontab_month_of_year: '',
                    crontab_timezone: 'UTC',
                  }
                : newScheduleType === 'crontab'
                  ? {
                      interval_every: '',
                      interval_period: 'minutes',
                      crontab_timezone: 'UTC',
                    }
                  : {
                      // Clear both when switching to 'none'
                      interval_every: '',
                      interval_period: 'minutes',
                      crontab_minute: '',
                      crontab_hour: '',
                      crontab_day_of_week: '',
                      crontab_day_of_month: '',
                      crontab_month_of_year: '',
                      crontab_timezone: 'UTC',
                    }),
            }));
          }}
          options={[
            { key: 'none', text: 'None (One-time task)', value: 'none' },
            { key: 'interval', text: 'Interval (Every X minutes/hours/days)', value: 'interval' },
            { key: 'crontab', text: 'Cron (Advanced scheduling)', value: 'crontab' },
          ]}
          selection
          placeholder="Select schedule type..."
        />
        <div style={{ fontSize: '0.9em', color: '#666', marginTop: '4px' }}>
          {formData.schedule_type === 'none' && 'Task will run once when manually triggered'}
          {formData.schedule_type === 'interval' && 'Task will run repeatedly at regular intervals'}
          {formData.schedule_type === 'crontab' && 'Task will run based on cron expression (e.g., daily at 9 AM)'}
        </div>
      </Form.Field>

      {((task?.parameter_schemas && task?.parameter_schemas.length !== 0) || 
        (((task as any)?.registry_task?.default_kwargs || (task as any)?.default_kwargs) && 
         Object.keys((task as any)?.registry_task?.default_kwargs || (task as any)?.default_kwargs || {}).length > 0)) && (
        <Form.Field>
          <label>Parameters</label>
          <ParameterDisplay
            parameters={formData.kwargs}
            onEditParameter={(key, newValue) => {
              try {
                const currentParams = JSON.parse(formData.kwargs || '{}');
                currentParams[key] = newValue;
                setFormData(prev => ({ ...prev, kwargs: JSON.stringify(currentParams, null, 2) }));
              } catch (error) {
                console.error('Error updating parameter:', error);
              }
            }}
            onRemoveParameter={key => {
              try {
                const currentParams = JSON.parse(formData.kwargs || '{}');
                delete currentParams[key];
                setFormData(prev => ({ ...prev, kwargs: JSON.stringify(currentParams, null, 2) }));
              } catch (error) {
                console.error('Error removing parameter:', error);
              }
            }}
            allowRemove={false}
          />
          <div style={{ fontSize: '0.8em', color: '#666', marginTop: '8px' }}>
            Edit the values for the defined parameters above. Supports inline date arithmetic like:{' '}
            {`{"start_date": "2024-01-01+7d"}`}
          </div>
        </Form.Field>
      )}
      {formData.schedule_type === 'interval' && (
        <Form.Field>
          <label>Interval Schedule Configuration</label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span style={{ fontSize: '0.9em', color: '#666' }}>Run every</span>
            <Input
              type="number"
              value={formData.interval_every}
              onChange={(_, { value }) => setFormData(prev => ({ ...prev, interval_every: value }))}
              placeholder="1"
              min="1"
              style={{ width: '80px' }}
            />
            <Dropdown
              value={formData.interval_period}
              onChange={(_, { value }) =>
                setFormData(prev => ({ ...prev, interval_period: value as string }))
              }
              options={[
                { key: 'seconds', text: 'Second(s)', value: 'seconds' },
                { key: 'minutes', text: 'Minute(s)', value: 'minutes' },
                { key: 'hours', text: 'Hour(s)', value: 'hours' },
                { key: 'days', text: 'Day(s)', value: 'days' },
              ]}
              style={{ width: '120px' }}
            />
          </div>
          <div style={{ fontSize: '0.8em', color: '#666', marginTop: '4px' }}>
            Example: "5 minutes" means the task runs every 5 minutes
          </div>
        </Form.Field>
      )}

      {formData.schedule_type === 'crontab' && (
        <Form.Field>
          <label>Cron Schedule Configuration</label>
          <div style={{ marginBottom: '8px' }}>
            <div style={{ fontSize: '0.9em', color: '#666', marginBottom: '8px' }}>
              Cron format: Minute Hour Day Month Weekday (use * for "any")
            </div>
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '18%' }}>
                <Input
                  value={formData.crontab_minute}
                  onChange={(_, { value }) => setFormData(prev => ({ ...prev, crontab_minute: value }))}
                  placeholder="*"
                  style={{ width: '100%' }}
                />
                <small style={{ fontSize: '0.7em', color: '#999', marginTop: '2px' }}>Minute</small>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '18%' }}>
                <Input
                  value={formData.crontab_hour}
                  onChange={(_, { value }) => setFormData(prev => ({ ...prev, crontab_hour: value }))}
                  placeholder="*"
                  style={{ width: '100%' }}
                />
                <small style={{ fontSize: '0.7em', color: '#999', marginTop: '2px' }}>Hour</small>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '18%' }}>
                <Input
                  value={formData.crontab_day_of_month}
                  onChange={(_, { value }) =>
                    setFormData(prev => ({ ...prev, crontab_day_of_month: value }))
                  }
                  placeholder="*"
                  style={{ width: '100%' }}
                />
                <small style={{ fontSize: '0.7em', color: '#999', marginTop: '2px' }}>Day</small>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '18%' }}>
                <Input
                  value={formData.crontab_month_of_year}
                  onChange={(_, { value }) =>
                    setFormData(prev => ({ ...prev, crontab_month_of_year: value }))
                  }
                  placeholder="*"
                  style={{ width: '100%' }}
                />
                <small style={{ fontSize: '0.7em', color: '#999', marginTop: '2px' }}>Month</small>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '18%' }}>
                <Input
                  value={formData.crontab_day_of_week}
                  onChange={(_, { value }) =>
                    setFormData(prev => ({ ...prev, crontab_day_of_week: value }))
                  }
                  placeholder="*"
                  style={{ width: '100%' }}
                />
                <small style={{ fontSize: '0.7em', color: '#999', marginTop: '2px' }}>Weekday</small>
              </div>
            </div>
          </div>
          <div style={{ fontSize: '0.8em', color: '#666', marginTop: '8px' }}>
            Examples: "0 9 * * *" = Daily at 9 AM UTC, "30 14 * * 1" = Every Monday at 2:30 PM UTC
            <br />
            <strong>Note:</strong> All times are in UTC timezone
          </div>
        </Form.Field>
      )}

      <div
        style={{
          display: 'flex',
          gap: '8px',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '16px',
        }}
      >
        <Checkbox
          label="Enabled"
          checked={formData.enabled}
          onChange={(_, { checked }) =>
            setFormData(prev => ({ ...prev, enabled: checked || false }))
          }
        />
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button primary onClick={handleSubmit} loading={loading}>
            Save Changes
          </Button>
        </div>
      </div>
    </Form>
  );
};

export default EditTaskForm;
